import React, { useState } from 'react';
import { T, font, mono, trackUrl } from '../utils/theme';
import * as api from '../utils/api';
import { Card, Sect, Btn, Inp, Sel, SearchBar, StatusBadge, SBadge, IcCheck, IcPrint, IcMsg, Modal, matchesSearch } from './UI';
import { money } from '../utils/money';

function TrackingLink({ tracking, courier }) {
  const url = trackUrl(courier, tracking);
  if (!url || tracking === '—') return <span style={{ fontFamily: mono, fontSize: 12, color: T.muted }}>{tracking}</span>;
  return <a href={url} target="_blank" rel="noopener noreferrer" style={{ fontFamily: mono, fontSize: 12, color: T.accent, textDecoration: 'underline' }}>{tracking}</a>;
}

const COURIERS = ['DHL', 'FedEx', 'UPS', 'Canada Post'];

export default function AdminShipments({ lang, products, allProducts, shipments, allSuppliers, shipmentComments, reload, Lk }) {
  const [search, setSearch] = useState('');
  const [receiveQtys, setReceiveQtys] = useState({});
  const [openComment, setOpenComment] = useState(null);
  const [commentText, setCommentText] = useState('');

  // Supplier filter
  const [supplierFilter, setSupplierFilter] = useState('');

  // Untracked bulk receive state
  const [selectedItems, setSelectedItems] = useState({});
  const [showQuickModal, setShowQuickModal] = useState(false);
  const [hoveredRowKey, setHoveredRowKey] = useState(null);
  const [qmTracking, setQmTracking] = useState('');
  const [qmCourier, setQmCourier] = useState('');
  const [qmShippingCost, setQmShippingCost] = useState('');
  const [qmItemQtys, setQmItemQtys] = useState({});
  const [qmItemPrices, setQmItemPrices] = useState({});
  const [quickReceiving, setQuickReceiving] = useState(false);
  const [receiptSummary, setReceiptSummary] = useState(null); // { price_changes, new_products, date }
  const [qmExtraItems, setQmExtraItems] = useState([]);
  const [extraSearch, setExtraSearch] = useState('');
  const [showExtraDropdown, setShowExtraDropdown] = useState(false);

  const activeShipments = shipments.filter(s => s.status === 'shipped');

  // Untracked: ordering status, no shipment created yet
  const allUntracked = products.filter(p => p.status === 'ordering' && !p.shipment_id && p.assigned_supplier);
  const untrackedFiltered = allUntracked.filter(p => !supplierFilter || p.assigned_supplier === supplierFilter);

  const matchFilter = (s) => {
    if (!search) return true;
    const q = search.toLowerCase();
    const sup = allSuppliers.find(x => x.id === s.supplier_id);
    const prods = (s.items || []).map(i => (allProducts || products).find(p => p.id === i.product_id)).filter(Boolean);
    return (s.tracking || '').toLowerCase().includes(q) || (sup?.name || '').toLowerCase().includes(q) || prods.some(p => p.name.toLowerCase().includes(q) || (p.sku || '').toLowerCase().includes(q));
  };

  const filteredShipments = activeShipments
    .filter(s => !supplierFilter || s.supplier_id === supplierFilter)
    .filter(matchFilter);

  const doReceiveItem = async (shipId, productId) => {
    const key = `${shipId}-${productId}`;
    const item = shipments.find(s => s.id === shipId)?.items?.find(i => i.product_id === productId);
    const qty = receiveQtys[key] !== undefined ? parseInt(receiveQtys[key]) : (item?.ship_qty || 0);
    await api.receiveShipmentItem(shipId, productId, qty);
    await reload();
  };

  const doReceiveAll = async (shipId) => {
    await api.receiveAllShipment(shipId);
    await reload();
  };

  const doSendComment = async (shipId) => {
    if (!commentText.trim()) return;
    await api.addShipmentComment({ shipment_id: shipId, author: 'Admin', author_type: 'admin', text: commentText.trim() });
    setCommentText('');
    await reload();
  };

  const doPrint = (s) => {
    const sup = allSuppliers.find(x => x.id === s.supplier_id);
    const prods = (s.items || []).map(i => {
      const p = (allProducts || products).find(x => x.id === i.product_id);
      return p ? { name: p.name, sku: p.sku, qty: i.ship_qty, price: i.unit_price || p.quotes?.[s.supplier_id]?.price || p.cost_price || 0 } : null;
    }).filter(Boolean);
    const w = window.open('', '_blank');
    if (!w) return;
    w.document.write(`<html><head><title>Shipment ${s.tracking}</title><style>body{font-family:Arial,sans-serif;padding:40px;color:#333}table{width:100%;border-collapse:collapse;margin-top:20px}th{background:#222;color:#fff;padding:10px 14px;text-align:left;font-size:13px}td{padding:8px 14px;border-bottom:1px solid #ddd;font-size:13px}.header{display:flex;justify-content:space-between;margin-bottom:30px}.info{text-align:right;font-size:12px;line-height:1.6}.total{text-align:right;padding:10px 14px;font-weight:bold;font-size:14px}</style></head><body>`);
    w.document.write(`<div class="header"><div><strong>Tan Star Trade</strong></div><div class="info"><strong>9264-9854 Quebec Inc.</strong><br>8500 Boulevard Taschereau, Unit 10<br>Brossard, Quebec, J4X 2T4, Canada<br>(450)-465-5506</div></div>`);
    w.document.write(`<div style="margin-bottom:10px"><strong>Shipment:</strong> ${s.tracking} | <strong>Courier:</strong> ${s.courier} | <strong>From:</strong> ${sup?.name} | <strong>Date:</strong> ${s.created_at}</div>`);
    w.document.write(`<table><thead><tr><th>Product</th><th>SKU</th><th>Qty</th><th>Unit Price</th><th>Sub Total</th></tr></thead><tbody>`);
    let grandTotal = 0;
    prods.forEach(p => { const sub = p.price * p.qty; grandTotal += sub; w.document.write(`<tr><td>${p.name}</td><td>${p.sku}</td><td>${p.qty}</td><td>${money(p.price)}</td><td>${money(sub)}</td></tr>`); });
    w.document.write(`</tbody></table>`);
    w.document.write(`<div class="total">Subtotal: ${money(grandTotal)}</div>`);
    if (s.shipping_cost) w.document.write(`<div class="total">Shipping: ${money(s.shipping_cost)}</div>`);
    w.document.write(`<div class="total" style="font-size:16px;border-top:2px solid #333;margin-top:4px;padding-top:8px">Total: ${money((grandTotal + (s.shipping_cost || 0)))}</div>`);
    w.document.write(`</body></html>`);
    w.document.close();
    w.print();
  };

  // Bulk receive handlers
  const toggleSelectItem = (pid) => {
    setSelectedItems(prev => ({ ...prev, [pid]: !prev[pid] }));
  };

  const toggleSelectAll = () => {
    const allSelected = untrackedFiltered.every(p => selectedItems[p.id]);
    if (allSelected) {
      setSelectedItems({});
    } else {
      const next = {};
      untrackedFiltered.forEach(p => { next[p.id] = true; });
      setSelectedItems(next);
    }
  };

  const selectedProductIds = Object.entries(selectedItems).filter(([, v]) => v).map(([k]) => k)
    .filter(pid => allUntracked.some(p => p.id === pid));
  const selectedCount = selectedProductIds.length;

  const openQuickModal = () => {
    const qtys = {};
    const prices = {};
    selectedProductIds.forEach(pid => {
      const p = allUntracked.find(x => x.id === pid);
      if (p) {
        qtys[pid] = String(p.assigned_qty || '');
        const quotePrice = p.quotes?.[p.assigned_supplier]?.price;
        const fallback = p.cost_price;
        prices[pid] = String(quotePrice != null ? quotePrice : (fallback != null ? fallback : ''));
      }
    });
    setQmItemQtys(qtys);
    setQmItemPrices(prices);
    setQmTracking('');
    setQmCourier('');
    setQmShippingCost('');
    setQmExtraItems([]);
    setExtraSearch('');
    setShowExtraDropdown(false);
    setShowQuickModal(true);
  };

  const addExtraItem = (product) => {
    if (qmExtraItems.some(i => i.product_id === product.id)) return;
    const suppIds = [...new Set(selectedProductIds.map(pid => allUntracked.find(p => p.id === pid)?.assigned_supplier).filter(Boolean))];
    const defaultSuppId = product.assigned_supplier || suppIds[0] || activeSuppliers[0]?.id || '';
    setQmExtraItems(prev => [...prev, {
      uid: `${product.id}-${Date.now()}`,
      product_id: product.id,
      qty: '1',
      price: String(product.cost_price != null ? product.cost_price : ''),
      supplier_id: defaultSuppId,
    }]);
    setExtraSearch('');
    setShowExtraDropdown(false);
  };

  const removeExtraItem = (uid) => setQmExtraItems(prev => prev.filter(i => i.uid !== uid));
  const updateExtraItem = (uid, field, val) => setQmExtraItems(prev => prev.map(i => i.uid === uid ? ({ ...i, [field]: val }) : i));

  const doQuickReceive = async () => {
    setQuickReceiving(true);
    const selectedProds = selectedProductIds.map(pid => allUntracked.find(p => p.id === pid)).filter(Boolean);
    // Group by supplier so each supplier gets their own shipment record
    const bySupplier = {};
    selectedProds.forEach(p => {
      if (!bySupplier[p.assigned_supplier]) bySupplier[p.assigned_supplier] = [];
      bySupplier[p.assigned_supplier].push({
        product_id: p.id,
        received_qty: parseInt(qmItemQtys[p.id]) || p.assigned_qty || 0,
        unit_price: parseFloat(qmItemPrices[p.id]) || 0,
      });
    });
    qmExtraItems.forEach(item => {
      const sid = item.supplier_id || Object.keys(bySupplier)[0] || '';
      if (!bySupplier[sid]) bySupplier[sid] = [];
      bySupplier[sid].push({
        product_id: item.product_id,
        received_qty: parseInt(item.qty) || 0,
        unit_price: parseFloat(item.price) || 0,
      });
    });
    const allPriceChanges = [];
    const allNewProducts = [];
    for (const [supplierId, items] of Object.entries(bySupplier)) {
      if (!items.length) continue;
      const result = await api.quickReceive({
        supplier_id: supplierId,
        tracking: qmTracking.trim() || '—',
        courier: qmCourier || '—',
        shipping_cost: parseFloat(qmShippingCost) || 0,
        items,
      });
      if (result?.price_changes) allPriceChanges.push(...result.price_changes);
      if (result?.new_products) allNewProducts.push(...result.new_products);
    }
    setSelectedItems({});
    setQmExtraItems([]);
    setShowQuickModal(false);
    setQuickReceiving(false);
    await reload();
    if (allPriceChanges.length > 0 || allNewProducts.length > 0) {
      setReceiptSummary({ price_changes: allPriceChanges, new_products: allNewProducts, date: new Date().toLocaleDateString() });
    }
  };

  const activeSuppliers = allSuppliers.filter(s => s.active);

  return (
    <div>
      <h2 style={{ margin: '0 0 18px', fontSize: 22, fontWeight: 900, color: T.text }}>
        {Lk('shipments')} ({activeShipments.length})
        {allUntracked.length > 0 && <span style={{ fontSize: 13, fontWeight: 600, color: T.warn, marginLeft: 12 }}>· {allUntracked.length} untracked</span>}
      </h2>

      {/* Filters row */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <Sel value={supplierFilter} onChange={e => { setSupplierFilter(e.target.value); setSelectedItems({}); }} style={{ minWidth: 160 }}>
          <option value="">{lang === 'zh' ? '所有供应商' : 'All Suppliers'}</option>
          {activeSuppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
        </Sel>
        <SearchBar sticky value={search} onChange={setSearch} placeholder={`${Lk('searchProduct')} / ${Lk('searchTracking')} / ${Lk('searchSupplier')}`} style={{ flex: 1 }} />
      </div>

      {/* ── In-Transit Shipments ── */}
      {filteredShipments.length === 0 && allUntracked.length === 0 ? (
        <div style={{ color: T.dim, fontSize: 13, fontStyle: 'italic', padding: 20 }}>{Lk('noData')}</div>
      ) : filteredShipments.map(s => {
        const sup = allSuppliers.find(x => x.id === s.supplier_id);
        const items = (s.items || []).map(i => {
          const p = (allProducts || products).find(x => x.id === i.product_id);
          return { ...i, product: p };
        }).filter(i => i.product);

        const hasMismatch = items.some(i => i.received && i.received_qty !== i.ship_qty);
        const comments = (shipmentComments || []).filter(c => c.shipment_id === s.id);
        const hasComments = comments.length > 0;
        const isCommentOpen = openComment === s.id;

        let subtotal = 0;
        items.forEach(i => {
          const price = i.unit_price || i.product?.quotes?.[s.supplier_id]?.price || i.product?.cost_price || 0;
          subtotal += price * (i.ship_qty || 0);
        });
        const total = subtotal + (s.shipping_cost || 0);

        return (
          <Card key={s.id} hoverBg={!hasMismatch} style={{ marginBottom: 12, borderColor: hasMismatch ? T.orange + '80' : T.border, background: hasMismatch ? T.orangeBg : T.surface }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 11, color: T.dim, fontFamily: mono }}>{s.created_at}</span>
                <span style={{ fontSize: 12, color: T.muted, fontWeight: 600 }}>{sup?.name}</span>
                <TrackingLink tracking={s.tracking} courier={s.courier} />
                {s.courier && s.courier !== '—' && <SBadge label={s.courier} color={T.muted} bg={T.surfaceAlt} />}
                {hasComments && <span style={{ width: 10, height: 10, borderRadius: 5, background: T.warn, display: 'inline-block', flexShrink: 0 }} title="Has comments" />}
                {hasMismatch && <SBadge label={Lk('mismatch')} color={T.orange} bg={T.orangeBg} />}
              </div>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <Btn variant="ghostSm" onClick={() => { setOpenComment(isCommentOpen ? null : s.id); setCommentText(''); }}>{IcMsg}</Btn>
                <Btn variant="ghostSm" onClick={() => doPrint(s)}>{IcPrint} {Lk('print')}</Btn>
                <Btn variant="successSm" onClick={() => doReceiveAll(s.id)}>{IcCheck} {Lk('receivedAll')}</Btn>
              </div>
            </div>

            <table style={{ width: '100%', fontSize: 12, fontFamily: font, borderCollapse: 'collapse' }}>
              <thead><tr>
                {[Lk('productName'), Lk('sku'), Lk('shipQty'), Lk('cost'), 'Subtotal', Lk('qtyReceived'), Lk('status'), Lk('action')].map((h, i) => (
                  <th key={i} style={{ textAlign: 'left', padding: '6px 10px', background: T.surfaceAlt, color: T.dim, fontSize: 10, fontWeight: 800, textTransform: 'uppercase', borderBottom: `1px solid ${T.border}` }}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {items.map(item => {
                  const key = `${s.id}-${item.product_id}`;
                  const isReceived = item.received;
                  const rQty = receiveQtys[key] !== undefined ? receiveQtys[key] : (item.ship_qty || '');
                  const mismatch = isReceived && item.received_qty !== item.ship_qty;
                  const price = item.unit_price || item.product?.quotes?.[s.supplier_id]?.price || item.product?.cost_price || 0;
                  const lineSub = price * (item.ship_qty || 0);

                  return (
                    <tr key={item.product_id} style={{ background: mismatch ? T.orangeBg : isReceived ? T.successBg : hoveredRowKey === `${s.id}-${item.product_id}` ? '#EFF6FF' : 'transparent' }} onMouseEnter={() => setHoveredRowKey(`${s.id}-${item.product_id}`)} onMouseLeave={() => setHoveredRowKey(null)}>
                      <td style={{ padding: '8px 10px', borderBottom: `1px solid ${T.border}`, fontWeight: 600, color: T.text }}>{item.product?.deleted ? `${item.product.name} (Deleted)` : item.product?.name}</td>
                      <td style={{ padding: '8px 10px', borderBottom: `1px solid ${T.border}`, fontFamily: mono, color: T.muted, fontSize: 11 }}>{item.product?.sku}</td>
                      <td style={{ padding: '8px 10px', borderBottom: `1px solid ${T.border}`, fontFamily: mono, color: T.muted }}>{item.ship_qty}</td>
                      <td style={{ padding: '8px 10px', borderBottom: `1px solid ${T.border}`, fontFamily: mono, color: T.muted }}>{money(price)}</td>
                      <td style={{ padding: '8px 10px', borderBottom: `1px solid ${T.border}`, fontFamily: mono, color: T.text }}>{money(lineSub)}</td>
                      <td style={{ padding: '8px 10px', borderBottom: `1px solid ${T.border}` }}>
                        {isReceived ? (
                          <span style={{ fontFamily: mono, color: mismatch ? T.orange : T.success }}>
                            {item.received_qty} {mismatch && <SBadge label={Lk('mismatch')} color={T.orange} bg={T.orangeBg} />}
                          </span>
                        ) : (
                          <input type="number" value={rQty} onChange={e => setReceiveQtys({ ...receiveQtys, [key]: e.target.value })}
                            style={{ fontFamily: mono, fontSize: 12, padding: '4px 8px', width: 60, background: T.bg, border: `1px solid ${T.border}`, borderRadius: 4, color: T.text, outline: 'none' }} />
                        )}
                      </td>
                      <td style={{ padding: '8px 10px', borderBottom: `1px solid ${T.border}` }}>
                        {isReceived ? <SBadge label={Lk('received')} color={T.success} bg={T.successBg} /> : <StatusBadge status="on_the_way" lang={lang} />}
                      </td>
                      <td style={{ padding: '8px 10px', borderBottom: `1px solid ${T.border}` }}>
                        {!isReceived && <Btn variant="successSm" onClick={() => doReceiveItem(s.id, item.product_id)}>{IcCheck} {Lk('received')}</Btn>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 20, marginTop: 8, padding: '8px 10px', borderTop: `1px solid ${T.border}` }}>
              <span style={{ fontSize: 12, color: T.muted }}>Subtotal: <span style={{ fontFamily: mono, fontWeight: 700, color: T.text }}>{money(subtotal)}</span></span>
              <span style={{ fontSize: 12, color: T.muted }}>Shipping: <span style={{ fontFamily: mono, fontWeight: 700, color: T.text }}>{money((s.shipping_cost || 0))}</span></span>
              <span style={{ fontSize: 13, color: T.accent, fontWeight: 800 }}>Total: <span style={{ fontFamily: mono }}>{money(total)}</span></span>
            </div>

            {isCommentOpen && (
              <div style={{ marginTop: 10, padding: 12, background: T.bg, borderRadius: 8, border: `1px solid ${T.border}` }}>
                <div style={{ fontSize: 10, fontWeight: 800, color: T.dim, textTransform: 'uppercase', marginBottom: 8 }}>{lang === 'zh' ? '物流备注' : 'Shipment Notes'}</div>
                {comments.map(c => (
                  <div key={c.id} style={{ fontSize: 12, padding: '4px 0', borderBottom: `1px solid ${T.border}20` }}>
                    <span style={{ fontWeight: 700, color: c.author_type === 'admin' ? T.accent : T.success }}>{c.author}: </span>
                    <span style={{ color: T.text }}>{c.text}</span>
                    <span style={{ color: T.dim, fontSize: 10, marginLeft: 6 }}>{c.created_at?.split('T')[0]}</span>
                  </div>
                ))}
                <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                  <input value={commentText} onChange={e => setCommentText(e.target.value)} placeholder={lang === 'zh' ? '添加备注...' : 'Add note...'}
                    onKeyDown={e => { if (e.key === 'Enter') doSendComment(s.id); }}
                    style={{ flex: 1, fontFamily: font, fontSize: 12, padding: '7px 10px', background: T.surface, border: `1px solid ${T.border}`, borderRadius: 6, color: T.text, outline: 'none' }} />
                  <Btn variant="small" style={{ padding: '5px 10px', fontSize: 11 }} onClick={() => doSendComment(s.id)}>{lang === 'zh' ? '发送' : 'Send'}</Btn>
                </div>
              </div>
            )}
          </Card>
        );
      })}

      {/* ── Untracked Items ── */}
      {allUntracked.length > 0 && (
        <Sect title={`${lang === 'zh' ? '待接收（无物流单）' : 'Untracked Items'} (${untrackedFiltered.length})`}
          right={selectedCount > 0 ? (
            <Btn variant="small" onClick={openQuickModal}>{IcCheck} {lang === 'zh' ? `接收所选 (${selectedCount})` : `Receive Selected (${selectedCount})`}</Btn>
          ) : null}>
          <Card style={{ padding: 0, overflow: 'hidden' }}>
            <table style={{ width: '100%', fontSize: 12, fontFamily: font, borderCollapse: 'collapse' }}>
              <thead><tr>
                <th style={{ width: 36, padding: '8px 12px', background: T.surfaceAlt, borderBottom: `1px solid ${T.border}` }}>
                  <input type="checkbox"
                    checked={untrackedFiltered.length > 0 && untrackedFiltered.every(p => selectedItems[p.id])}
                    onChange={toggleSelectAll}
                    style={{ cursor: 'pointer', accentColor: T.success }} />
                </th>
                {[Lk('productName'), Lk('sku'), lang === 'zh' ? '供应商' : 'Supplier', lang === 'zh' ? '数量' : 'Qty Ordered'].map((h, i) => (
                  <th key={i} style={{ textAlign: 'left', padding: '8px 12px', background: T.surfaceAlt, color: T.dim, fontSize: 10, fontWeight: 800, textTransform: 'uppercase', borderBottom: `1px solid ${T.border}` }}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {untrackedFiltered.length === 0 ? (
                  <tr><td colSpan={5} style={{ padding: '16px 12px', color: T.dim, fontStyle: 'italic', fontSize: 12 }}>
                    {lang === 'zh' ? '此供应商无待接收商品' : 'No untracked items for selected supplier'}
                  </td></tr>
                ) : untrackedFiltered.map(p => {
                  const sup = allSuppliers.find(s => s.id === p.assigned_supplier);
                  const isSelected = !!selectedItems[p.id];
                  return (
                    <tr key={p.id} onClick={() => toggleSelectItem(p.id)} style={{ background: isSelected ? `${T.success}18` : 'transparent', cursor: 'pointer', borderBottom: `1px solid ${T.border}` }}>
                      <td style={{ padding: '10px 12px' }}>
                        <input type="checkbox" checked={isSelected} onChange={() => toggleSelectItem(p.id)}
                          onClick={e => e.stopPropagation()}
                          style={{ cursor: 'pointer', accentColor: T.success }} />
                      </td>
                      <td style={{ padding: '10px 12px', fontWeight: 600, color: T.text }}>{p.name}</td>
                      <td style={{ padding: '10px 12px', fontFamily: mono, color: T.muted, fontSize: 11 }}>{p.sku}</td>
                      <td style={{ padding: '10px 12px', color: T.muted }}>{sup?.name || '—'}</td>
                      <td style={{ padding: '10px 12px', fontFamily: mono, color: T.text }}>{p.assigned_qty}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Card>
          {untrackedFiltered.length > 0 && (
            <div style={{ fontSize: 11, color: T.dim, marginTop: 6 }}>
              {lang === 'zh' ? '勾选商品后点击"接收所选"以创建收货记录' : 'Select items and click "Receive Selected" to create a Past Orders record'}
            </div>
          )}
        </Sect>
      )}

      {/* ── Receipt Summary Modal ── */}
      <Modal open={!!receiptSummary} onClose={() => setReceiptSummary(null)}>
        {receiptSummary && (() => {
          const { price_changes, new_products, date } = receiptSummary;

          const doPrintSummary = () => {
            const w = window.open('', '_blank');
            if (!w) return;
            w.document.write(`<html><head><title>Price Update Summary – ${date}</title><style>
              body{font-family:Arial,sans-serif;padding:40px;color:#000;font-size:13px}
              h1{font-size:18px;margin:0 0 4px}
              .sub{font-size:12px;color:#555;margin-bottom:30px}
              h2{font-size:13px;font-weight:800;text-transform:uppercase;letter-spacing:.05em;color:#333;margin:24px 0 8px;padding-bottom:4px;border-bottom:2px solid #000}
              table{width:100%;border-collapse:collapse;margin-bottom:16px}
              th{background:#eee;padding:7px 10px;font-size:11px;font-weight:800;text-transform:uppercase;color:#333;border-bottom:1px solid #999}
              th.l{text-align:left} th.r{text-align:right}
              td{padding:7px 10px;border-bottom:1px solid #ddd;font-size:13px}
              td.l{text-align:left} td.r{text-align:right;font-family:monospace}
              td.name{font-weight:600} td.newprice{font-weight:800;font-size:14px;font-family:monospace;text-align:right}
              td.change{font-family:monospace;text-align:right;font-weight:700}
              @media print{body{padding:20px}}
            </style></head><body>`);
            w.document.write(`<h1>Price Update Summary</h1><div class="sub">Received: ${date} &nbsp;·&nbsp; Tan Star Trade</div>`);
            if (price_changes.length > 0) {
              w.document.write(`<h2>Products with Price Changes (${price_changes.length})</h2>`);
              w.document.write(`<table><thead><tr><th class="l">Product</th><th class="r">New Price</th><th class="r">Previous Price</th><th class="r">Change</th></tr></thead><tbody>`);
              price_changes.forEach(p => {
                const diff = p.new_price - p.previous_price;
                const changeStr = diff >= 0 ? `+${money(diff)}` : `-${money(Math.abs(diff))}`;
                w.document.write(`<tr><td class="name l">${p.product_name}</td><td class="newprice">${money(p.new_price)}</td><td class="r">${money(p.previous_price)}</td><td class="change">${changeStr}</td></tr>`);
              });
              w.document.write(`</tbody></table>`);
            }
            if (new_products.length > 0) {
              w.document.write(`<h2>New Products – First Time Purchased (${new_products.length})</h2>`);
              w.document.write(`<table><thead><tr><th class="l">Product</th><th class="r">New Price</th></tr></thead><tbody>`);
              new_products.forEach(p => {
                w.document.write(`<tr><td class="name l">${p.product_name}</td><td class="newprice">${money(p.new_price)}</td></tr>`);
              });
              w.document.write(`</tbody></table>`);
            }
            w.document.write(`</body></html>`);
            w.document.close();
            w.print();
          };

          return (
            <div>
              {/* Header row */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
                <div>
                  <div style={{ fontWeight: 900, fontSize: 17, color: T.text }}>
                    {lang === 'zh' ? '收货摘要' : 'Receive Summary'}
                  </div>
                  <div style={{ fontSize: 12, color: T.dim, marginTop: 2 }}>{date}</div>
                </div>
                <Btn variant="ghostSm" onClick={doPrintSummary}>{IcPrint} {lang === 'zh' ? '打印' : 'Print'}</Btn>
              </div>

              {/* Section 1: Price Changes */}
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 11, fontWeight: 800, textTransform: 'uppercase', color: T.dim, letterSpacing: '0.05em', marginBottom: 8, paddingBottom: 6, borderBottom: `2px solid ${T.border}` }}>
                  {lang === 'zh' ? `价格变动商品 (${price_changes.length})` : `Products with Price Changes (${price_changes.length})`}
                </div>
                {price_changes.length === 0 ? (
                  <div style={{ fontSize: 12, color: T.dim, fontStyle: 'italic', padding: '8px 0' }}>
                    {lang === 'zh' ? '本次收货无价格变动' : 'No price changes in this receipt'}
                  </div>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr>
                        {[lang === 'zh' ? '商品' : 'Product', lang === 'zh' ? '新价' : 'New Price', lang === 'zh' ? '原价' : 'Previous Price', lang === 'zh' ? '变动' : 'Change'].map((h, i) => (
                          <th key={i} style={{ textAlign: i === 0 ? 'left' : 'right', padding: '5px 8px', fontSize: 10, fontWeight: 800, textTransform: 'uppercase', color: T.dim, background: T.surfaceAlt, borderBottom: `1px solid ${T.border}` }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {price_changes.map((p, i) => {
                        const diff = p.new_price - p.previous_price;
                        const sign = diff > 0 ? '+' : '';
                        return (
                          <tr key={i} style={{ borderBottom: `1px solid ${T.border}` }}>
                            <td style={{ padding: '8px 8px', fontWeight: 600, color: T.text }}>{p.product_name}</td>
                            <td style={{ padding: '8px 8px', fontFamily: 'monospace', fontWeight: 800, fontSize: 14, color: T.text, textAlign: 'right' }}>{money(p.new_price)}</td>
                            <td style={{ padding: '8px 8px', fontFamily: 'monospace', color: T.muted, textAlign: 'right' }}>{money(p.previous_price)}</td>
                            <td style={{ padding: '8px 8px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 700, color: T.text, whiteSpace: 'nowrap' }}>
                              {sign}{money(diff)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>

              {/* Section 2: New Products */}
              <div style={{ marginBottom: 24 }}>
                <div style={{ fontSize: 11, fontWeight: 800, textTransform: 'uppercase', color: T.dim, letterSpacing: '0.05em', marginBottom: 8, paddingBottom: 6, borderBottom: `2px solid ${T.border}` }}>
                  {lang === 'zh' ? `新品（首次采购） (${new_products.length})` : `New Products – First Time Purchased (${new_products.length})`}
                </div>
                {new_products.length === 0 ? (
                  <div style={{ fontSize: 12, color: T.dim, fontStyle: 'italic', padding: '8px 0' }}>
                    {lang === 'zh' ? '本次无新品' : 'No new products in this receipt'}
                  </div>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr>
                        {[lang === 'zh' ? '商品' : 'Product', lang === 'zh' ? '采购价' : 'New Price'].map((h, i) => (
                          <th key={i} style={{ textAlign: i === 0 ? 'left' : 'right', padding: '5px 8px', fontSize: 10, fontWeight: 800, textTransform: 'uppercase', color: T.dim, background: T.surfaceAlt, borderBottom: `1px solid ${T.border}` }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {new_products.map((p, i) => (
                        <tr key={i} style={{ borderBottom: `1px solid ${T.border}` }}>
                          <td style={{ padding: '8px 8px', fontWeight: 600, color: T.text }}>{p.product_name}</td>
                          <td style={{ padding: '8px 8px', fontFamily: 'monospace', fontWeight: 800, fontSize: 14, color: T.text, textAlign: 'right' }}>{money(p.new_price)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Btn onClick={() => setReceiptSummary(null)}>
                  {lang === 'zh' ? '关闭' : 'Close'}
                </Btn>
              </div>
            </div>
          );
        })()}
      </Modal>

      {/* ── Quick Receive Modal ── */}
      <Modal open={showQuickModal} onClose={() => !quickReceiving && setShowQuickModal(false)}>
        <div>
          <div style={{ fontWeight: 900, fontSize: 17, color: T.text, marginBottom: 16 }}>
            {lang === 'zh' ? '批量接收' : 'Receive Items'}
          </div>

          {/* Item list with qty + price inputs */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 80px 90px 80px', gap: 0, marginBottom: 6 }}>
              <div style={{ fontSize: 10, fontWeight: 800, color: T.dim, textTransform: 'uppercase' }}>{lang === 'zh' ? '商品' : 'Item'}</div>
              <div style={{ fontSize: 10, fontWeight: 800, color: T.dim, textTransform: 'uppercase', textAlign: 'center' }}>{lang === 'zh' ? '数量' : 'Qty'}</div>
              <div style={{ fontSize: 10, fontWeight: 800, color: T.dim, textTransform: 'uppercase', textAlign: 'center' }}>{lang === 'zh' ? '单价' : 'Unit Price'}</div>
              <div style={{ fontSize: 10, fontWeight: 800, color: T.dim, textTransform: 'uppercase', textAlign: 'right' }}>{lang === 'zh' ? '小计' : 'Subtotal'}</div>
            </div>
            {selectedProductIds.map(pid => {
              const p = allUntracked.find(x => x.id === pid);
              if (!p) return null;
              const sup = allSuppliers.find(s => s.id === p.assigned_supplier);
              const rowQty = parseFloat(qmItemQtys[pid]) || 0;
              const rowPrice = parseFloat(qmItemPrices[pid]) || 0;
              const rowSubtotal = rowQty * rowPrice;
              return (
                <div key={pid} style={{ display: 'grid', gridTemplateColumns: '1fr 80px 90px 80px', gap: 8, alignItems: 'center', padding: '7px 0', borderBottom: `1px solid ${T.border}` }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: T.text }}>{p.name}</div>
                    <div style={{ fontSize: 11, color: T.dim }}>{sup?.name} · {p.sku}</div>
                  </div>
                  <input
                    type="number"
                    value={qmItemQtys[pid] || ''}
                    onChange={e => setQmItemQtys(prev => ({ ...prev, [pid]: e.target.value }))}
                    style={{ width: '100%', fontFamily: mono, fontSize: 13, padding: '5px 8px', background: T.bg, border: `1px solid ${T.border}`, borderRadius: 6, color: T.text, outline: 'none', textAlign: 'center' }}
                  />
                  <div style={{ position: 'relative' }}>
                    <span style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', color: T.dim, fontSize: 12, pointerEvents: 'none' }}>$</span>
                    <input
                      type="number"
                      step="0.01"
                      value={qmItemPrices[pid] || ''}
                      onChange={e => setQmItemPrices(prev => ({ ...prev, [pid]: e.target.value }))}
                      style={{ width: '100%', fontFamily: mono, fontSize: 13, padding: '5px 8px 5px 18px', background: T.bg, border: `1px solid ${T.border}`, borderRadius: 6, color: T.text, outline: 'none', textAlign: 'right', boxSizing: 'border-box' }}
                    />
                  </div>
                  <div style={{ fontFamily: mono, fontSize: 13, color: rowSubtotal > 0 ? T.text : T.dim, textAlign: 'right' }}>
                    {rowSubtotal > 0 ? `${money(rowSubtotal)}` : '—'}
                  </div>
                </div>
              );
            })}

            {/* Extra / additional items */}
            {qmExtraItems.length > 0 && (
              <div style={{ marginTop: 10, paddingTop: 10, borderTop: `2px dashed ${T.border}` }}>
                <div style={{ fontSize: 10, fontWeight: 800, color: T.dim, textTransform: 'uppercase', marginBottom: 6 }}>
                  {lang === 'zh' ? '额外商品' : 'Additional Items'}
                </div>
                {qmExtraItems.map(item => {
                  const p = (allProducts || []).find(x => x.id === item.product_id);
                  const rowQty = parseFloat(item.qty) || 0;
                  const rowPrice = parseFloat(item.price) || 0;
                  const rowSub = rowQty * rowPrice;
                  return (
                    <div key={item.uid} style={{ display: 'grid', gridTemplateColumns: '1fr 80px 90px 80px 28px', gap: 8, alignItems: 'center', padding: '7px 0', borderBottom: `1px solid ${T.border}` }}>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: T.text }}>{p?.name || '—'}</div>
                        <div style={{ fontSize: 11, color: T.dim }}>{p?.sku}</div>
                      </div>
                      <input type="number" value={item.qty}
                        onChange={e => updateExtraItem(item.uid, 'qty', e.target.value)}
                        style={{ width: '100%', fontFamily: mono, fontSize: 13, padding: '5px 8px', background: T.bg, border: `1px solid ${T.border}`, borderRadius: 6, color: T.text, outline: 'none', textAlign: 'center' }}
                      />
                      <div style={{ position: 'relative' }}>
                        <span style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', color: T.dim, fontSize: 12, pointerEvents: 'none' }}>$</span>
                        <input type="number" step="0.01" value={item.price}
                          onChange={e => updateExtraItem(item.uid, 'price', e.target.value)}
                          style={{ width: '100%', fontFamily: mono, fontSize: 13, padding: '5px 8px 5px 18px', background: T.bg, border: `1px solid ${T.border}`, borderRadius: 6, color: T.text, outline: 'none', textAlign: 'right', boxSizing: 'border-box' }}
                        />
                      </div>
                      <div style={{ fontFamily: mono, fontSize: 13, color: rowSub > 0 ? T.text : T.dim, textAlign: 'right' }}>
                        {rowSub > 0 ? `${money(rowSub)}` : '—'}
                      </div>
                      <button onClick={() => removeExtraItem(item.uid)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: T.muted, fontSize: 18, lineHeight: 1, padding: 0, width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        ×
                      </button>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Add product search */}
            <div style={{ position: 'relative', marginTop: 10 }}>
              <input
                type="text"
                value={extraSearch}
                onChange={e => { setExtraSearch(e.target.value); setShowExtraDropdown(true); }}
                onFocus={() => setShowExtraDropdown(true)}
                onBlur={() => setTimeout(() => setShowExtraDropdown(false), 150)}
                placeholder={lang === 'zh' ? '＋ 添加未列出的商品…' : '+ Add a product not on this list…'}
                disabled={quickReceiving}
                style={{ width: '100%', fontFamily: 'inherit', fontSize: 13, padding: '7px 12px', background: T.bg, border: `1px solid ${T.border}`, borderRadius: 6, color: T.text, outline: 'none', boxSizing: 'border-box' }}
              />
              {showExtraDropdown && extraSearch.length > 0 && (() => {
                const results = (allProducts || []).filter(p => {
                  if (p.deleted) return false;
                  if (selectedProductIds.includes(p.id)) return false;
                  if (qmExtraItems.some(i => i.product_id === p.id)) return false;
                  return matchesSearch(extraSearch, p.name) || matchesSearch(extraSearch, p.sku || '');
                }).slice(0, 8);
                return (
                  <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: T.surface, border: `1px solid ${T.border}`, borderRadius: 6, boxShadow: '0 4px 16px rgba(0,0,0,0.12)', zIndex: 200, maxHeight: 220, overflowY: 'auto' }}>
                    {results.length === 0 ? (
                      <div style={{ padding: '10px 12px', fontSize: 13, color: T.dim, fontStyle: 'italic' }}>
                        {lang === 'zh' ? '未找到商品' : 'No products found'}
                      </div>
                    ) : results.map(p => (
                      <div key={p.id} onMouseDown={() => addExtraItem(p)}
                        style={{ padding: '9px 12px', cursor: 'pointer', borderBottom: `1px solid ${T.border}`, display: 'flex', gap: 8, alignItems: 'center' }}
                        onMouseEnter={e => e.currentTarget.style.background = T.surfaceAlt}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                        <span style={{ fontSize: 13, fontWeight: 600, color: T.text, flex: 1 }}>{p.name}</span>
                        <span style={{ fontSize: 11, color: T.dim, fontFamily: mono }}>{p.sku}</span>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>

            {(() => {
              const itemsSubtotal = selectedProductIds.reduce((sum, pid) => {
                return sum + (parseFloat(qmItemQtys[pid]) || 0) * (parseFloat(qmItemPrices[pid]) || 0);
              }, 0) + qmExtraItems.reduce((sum, item) => {
                return sum + (parseFloat(item.qty) || 0) * (parseFloat(item.price) || 0);
              }, 0);
              const shippingCost = parseFloat(qmShippingCost) || 0;
              const grandTotal = itemsSubtotal + shippingCost;
              return (
                <div style={{ marginTop: 8, paddingTop: 8, borderTop: `1px solid ${T.border}` }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: T.muted, marginBottom: 4 }}>
                    <span>{lang === 'zh' ? '小计' : 'Subtotal'}</span>
                    <span style={{ fontFamily: mono, color: T.text }}>{money(itemsSubtotal)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: T.muted, marginBottom: 6 }}>
                    <span>{lang === 'zh' ? '运费' : 'Shipping'}</span>
                    <span style={{ fontFamily: mono, color: T.text }}>{money(shippingCost)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, fontWeight: 800, color: T.accent, borderTop: `1px solid ${T.border}`, paddingTop: 6 }}>
                    <span>{lang === 'zh' ? '总计' : 'Grand Total'}</span>
                    <span style={{ fontFamily: mono }}>{money(grandTotal)}</span>
                  </div>
                </div>
              );
            })()}
          </div>

          {/* Shipment details */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
            <Inp label={lang === 'zh' ? '物流单号（可选）' : 'Tracking # (optional)'} placeholder="—" value={qmTracking} onChange={e => setQmTracking(e.target.value)} />
            <Sel label={lang === 'zh' ? '快递公司（可选）' : 'Courier (optional)'} value={qmCourier} onChange={e => setQmCourier(e.target.value)}>
              <option value="">—</option>
              {COURIERS.map(c => <option key={c} value={c}>{c}</option>)}
            </Sel>
          </div>
          <Inp label={lang === 'zh' ? '运费（可选）' : 'Shipping Cost (optional)'} type="number" placeholder="0.00" value={qmShippingCost} onChange={e => setQmShippingCost(e.target.value)} style={{ maxWidth: 160 }} />

          <div style={{ display: 'flex', gap: 8, marginTop: 20, justifyContent: 'flex-end' }}>
            <Btn variant="ghost" onClick={() => setShowQuickModal(false)} disabled={quickReceiving}>{Lk('cancel')}</Btn>
            <Btn onClick={doQuickReceive} disabled={quickReceiving}>
              {quickReceiving ? (lang === 'zh' ? '处理中…' : 'Receiving…') : (lang === 'zh' ? '确认接收' : 'Confirm Receive')}
            </Btn>
          </div>
        </div>
      </Modal>
    </div>
  );
}
